import { useState, useEffect, useCallback } from 'react';
import { analyticsAPI, handleApiError } from '../services/api';
import config from '../config/environment';
import type { DashboardOverviewData } from '../types';

interface UseAnalyticsReturn {
  data: DashboardOverviewData | null;
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refetch: () => Promise<void>;
}

// Custom hook for dashboard analytics
export const useAnalytics = (autoRefresh: boolean = true): UseAnalyticsReturn => {
  const [data, setData] = useState<DashboardOverviewData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchData = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const response = await analyticsAPI.getDashboard();
      setData(response.data);
      setLastUpdated(new Date());
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();

    if (autoRefresh) {
      const interval = setInterval(fetchData, config.REFRESH_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [fetchData, autoRefresh]);

  return {
    data,
    loading,
    error,
    lastUpdated,
    refetch: fetchData
  };
};
